import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DialogSearch from '@/components/DialogSearch/DialogSearch';
import SearchTableRemote from '@/components/SearchTable/SearchTableRemote';
import Button from '@/components/UI/Button/Button';
import FilterSelectionOverview from '@/components/UI/FilterSelectionOverview/FilterSelectionOverview';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import * as Const from '@/helpers/constants';
import useAppLanguage from '@/hooks/useAppLanguage';
import useAuth from '@/hooks/useAuth';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import useError from '@/hooks/useError';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import useVisibilityMatrix from '@/hooks/useVisibilityMatrix';
import { setSubCampaignStatusesForms } from '@/redux/Forms/forms.action';
import { getSubCampaignStatusesForms } from '@/redux/Forms/forms.reducer';
import { setPlantName } from '@/redux/Header/header.action';
import { getPlantName } from '@/redux/Header/header.reducer';
import { setOverviewFilters } from '@/redux/OverviewFilters/overviewFilters.action';
import { getFilterSubCROverview, getRowsPerPageSubCR, getSortSubCROverview } from '@/redux/OverviewFilters/overviewFilters.reducer';
import { getSettingsInfo, getUserInfo } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import titleService from '@/services/titleService';
import { FiltersOverview, GlobalFilters, SortOverview, SubCampaignInfoOverview, User, UserSearch } from '@/types';
import { StatusCampaign, StatusSubCampaign, VisibilityMatrixAction, VisibilityMatrixFeature } from '@/types/Matrix';
import useQuery from '@/utils/hooks';
import styles from '../CampaignRequests/CampaignRequestsOpenSearch.module.scss';

const { filterPills: fp } = Const;
const filtersByRole = {
  [Const.CAMPAIGN_MANAGER]: [fp.allRequests, fp.representativeApplications, fp.notAssigned, fp.assignedToMe],
  [Const.CAMPAIGN_MANAGER_JV]: [fp.allRequests, fp.representativeApplications, fp.notAssigned, fp.assignedToMe],
  [Const.CART_QMB_PLANT_APPROVER]:
    [fp.allRequests, fp.representativeApplications, fp.requestsToApprove],
  [Const.QMB_PLANT_APPROVER_JV]:
    [fp.allRequests, fp.representativeApplications, fp.requestsToApprove],
  [Const.CART_PLANT_SUPERVISOR]:
    [fp.allRequests, fp.representativeApplications],
  [Const.ADMIN]:
    [fp.allRequests, fp.representativeApplications, fp.requestsToApprove, fp.notAssigned, fp.assignedToMe],
  OTHER: [fp.allRequests],
};

interface SubCROverviewProps {
  setGlobalFilters: (data: GlobalFilters) => void;
  filterSubCROverview: FiltersOverview;
  sortSubCROverview: SortOverview[];
  userInfo: User;
  subCampaignStatuses: string[];
  setStatuses: (data: string[]) => void;
  timeZone: string;
  pageSizeSetting: number;
  plantName: string;
  setHeaderPlantName: (data: string) => void;
}

const SubCROverview: React.FC<SubCROverviewProps> = (props) => {
  const { userInfo, timeZone, setGlobalFilters, subCampaignStatuses, setStatuses, filterSubCROverview,
    sortSubCROverview, pageSizeSetting, plantName, setHeaderPlantName } = props;
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const [selectedOption, setSelectedOption] = useState<SubCampaignInfoOverview[]>([]);
  const filterInUrl = useQuery().get('filter') || 'allRequests';
  const { handleCriticalError } = useError();
  const [subCampaignRequests, setSubCampaignRequests] = useState<SubCampaignInfoOverview[]>([]);
  const authService = useAuth();
  const [rowSelectedData, setRowSelectedData] = useState<SubCampaignInfoOverview | null>(null);
  const [filterSelected, setFilterSelected] = useState('');
  const [openCampaignManagerDialog, setOpenCampaignManagerDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [resetFilter, setResetFilter] = useState(0);
  const [resetData, setResetData] = useState(0);
  const { addToast } = useToastContext();
  const { goTo } = useGoTo();
  const checkPlantApprover = authService.checkRoles([Const.CART_QMB_PLANT_APPROVER, Const.QMB_PLANT_APPROVER_JV, Const.CART_PLANT_SUPERVISOR]);
  const { canSee } = useVisibilityMatrix();
  const { isButtonDisabled } = useCRPermissionMatrix(selectedOption[0]?.status, selectedOption[0]?.authorization);
  const canSeeCR = (action: VisibilityMatrixAction) => canSee({
    feature: VisibilityMatrixFeature.SUB_CR,
    action: action,
  }) || !isButtonDisabled(action);
  const checkAssignToCMDisabled = selectedOption.length !== 1 || !canSee({
    feature: VisibilityMatrixFeature.SUB_CR,
    action: VisibilityMatrixAction.ASSIGN_MANAGER,
    status: selectedOption[0]?.status as StatusCampaign | StatusSubCampaign, // TODO Missing type on status,
  });

  const handleFilterClick = (filter: string) => {
    setGlobalFilters({ pillFilterSubCampaignOverview: JSON.stringify(filter) });
    goTo(`${Const.ROUTES.SUB_CR_OVERVIEW}?filter=${filter}`);
  };

  const assignSubCR = (manager: UserSearch | null) => {
    const mngr = manager || userInfo.userCode;
    campaignServices.putAssignSubCR(rowSelectedData?.id, mngr)
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => {
        setResetData(resetData + 1);
        setSelectedOption([]);
      });
  };

  useEffect(() => {
    if (subCampaignStatuses.length === 0) {
      campaignServices.getStatuses(true).then((res) => {
        setStatuses(res.statuses);
      })
        .catch(handleCriticalError);
    }
  }, []);

  useEffect(() => {
    setIsLoading(!pageSizeSetting);
  }, [pageSizeSetting]);

  useEffect(() => {
    const userRole = authService.getUserRole(true);
    const filters = filtersByRole?.[userRole[0]] || filtersByRole.OTHER;

    let filter = filterInUrl;
    filter = filters.includes(filter) ? filter : 'allRequests';
    setFilterSelected(filter);

    if (filterInUrl !== filter) goTo(`${Const.ROUTES.SUB_CR_OVERVIEW}?filter=${filter}`);
  }, [authService, filterInUrl, goTo, lang, timeZone]);

  useEffect(() => {
    if (plantName && checkPlantApprover) {
      campaignServices.getPlantName(authService.userPlant).then((res) => {
        setHeaderPlantName(res.name);
      }).catch(handleCriticalError)
        .finally(() => setIsLoading(false));
    }

    if (checkPlantApprover) {
      titleService.updatePageTitle(t('titles.sub-campaign-overview-plant', { plant: plantName }));
    } else {
      titleService.updatePageTitle(t('titles.sub-campaign-overview'));
    }
    localStorage.setItem(Const.STORAGE.LAST_OVERVIEW, Const.LAST_OVERVIEW_VALUES.SUB_CAMPAIGN_OVERVIEW);
  }, [t, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const getData = (model: any) => {
    const selection = model.map((rowId) => subCampaignRequests.find((campaign) => rowId?.includes(campaign.id)))
      .filter((item) => item !== undefined);
    setSelectedOption(selection);
    setRowSelectedData(selection.at(-1));
  };

  const chipButtons = () => {
    const filters = filtersByRole?.[authService.getUserRole(true)[0]] || filtersByRole.OTHER;

    return (
      <>
        {filters.map((filterName) => (
          <Button
            key={`filter-${filterName}`}
            id={`filter-${filterName}`}
            handleClick={() => handleFilterClick(filterName)}
            buttonStyle="GreyFilter"
            btnText={t(`campaignRequests.${filterName === 'deleted' ? 'deletedSubCr' : filterName}`)}
            active={filterSelected === filterName}
          />
        ))}
      </>
    );
  };

  const badgeCount = Object.keys(filterSubCROverview?.filter((element) => element?.value && element?.value.length > 0) || {}).length;

  return (
    <>
      <div className={styles.CampaignDetail}>
        {isLoading && <div className={styles.Spinner}><Spinner isVisible /></div>}
        {!isLoading && (
          <>
            <FilterSelectionOverview badgeCount={badgeCount} isSubCROverview />
            <div className={styles.PageContent}>
              <div className={styles.ButtonsTable}>
                <div className={styles.ButtonsContentTableLeft}>
                  {chipButtons()}
                </div>
                <div className={styles.ButtonsContentTableRightSubCR}>
                  {canSeeCR(VisibilityMatrixAction.ASSIGN_MANAGER_OVERVIEW)
                    && (
                      <>
                        <SimpleButton
                          id="btn-affected-campaign-subCR"
                          color="primary"
                          disabled={checkAssignToCMDisabled}
                          buttonText={t('campaignRequests.assignManager')}
                          handleClick={() => setOpenCampaignManagerDialog(true)}
                        />
                        <SimpleButton
                          id="btn-affectedtome-campaign-subCR"
                          color="primary"
                          disabled={checkAssignToCMDisabled}
                          buttonText={t('campaignRequests.assignMe')}
                          handleClick={() => assignSubCR(null)}
                        />
                      </>
                    )}
                  <div className={styles.ButtonResetFilter}>
                    <IcoButtonSimple
                      id="btn-reset-campaign-subCR"
                      iconComponent={<i className="di icon-filter-zuruecksetzen" />}
                      tooltipText={t('campaignRequests.resetFilter')}
                      handleClick={() => setResetFilter(resetFilter + 1)}
                      disabled={badgeCount === 0}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.TableCampaigns}>
                <SearchTableRemote
                  isSubCROverview
                  chipFilter={filterInUrl}
                  setSelectedOption={getData}
                  selectedOption={selectedOption}
                  timeZone={timeZone}
                  setCampaignRequests={(subCampaignRequests: any[]) => setSubCampaignRequests(subCampaignRequests)}
                  filterColumnExtensions={[
                    { columnName: '', filteringEnabled: false },
                  ]}
                  columSorting={[{ columnName: 'creationDate', direction: Const.DESC_ORDER }]}
                  setFilters={setGlobalFilters}
                  resetFilter={resetFilter}
                  resetData={resetData}
                  filterOverview={filterSubCROverview}
                  sortOverview={sortSubCROverview}
                  campaignStatuses={subCampaignStatuses}
                  savedPageSize={pageSizeSetting}
                />
              </div>
            </div>
          </>
        )}
      </div>
      {openCampaignManagerDialog && (
        <DialogSearch
          plant={rowSelectedData?.plant}
          title={t('campaignRequests.assignManager')}
          close={() => setOpenCampaignManagerDialog(false)}
          updateOptionSelected={(managerSelected) => assignSubCR(managerSelected)}
          isRemote={false}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  userInfo: getUserInfo(state),
  timeZone: getSettingsInfo(state)?.timezone,
  filterSubCROverview: getFilterSubCROverview(state),
  sortSubCROverview: getSortSubCROverview(state),
  subCampaignStatuses: getSubCampaignStatusesForms(state),
  pageSizeSetting: getRowsPerPageSubCR(state),
  plantName: getPlantName(state),
});

const actionCreators = {
  setGlobalFilters: setOverviewFilters,
  setStatuses: setSubCampaignStatusesForms,
  setHeaderPlantName: setPlantName,
};

export default connect(mapStateToProps, actionCreators)(SubCROverview);
